import { useNavigate } from 'react-router-dom';
import useTimer from 'functions/hooks/useTimer';
import { useLocalStorage } from './useLocalStorage';
import { useAppSettings } from 'context/appsettingsContext';
import { getFirestore, getDoc, doc, Firestore } from 'firebase/firestore';
import { initializeApp, FirebaseOptions, FirebaseApp } from 'firebase/app';
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { getAuth, signInWithEmailAndPassword, signOut, Auth, onAuthStateChanged, browserSessionPersistence, setPersistence } from 'firebase/auth';

interface Props {
  children: React.ReactNode;
}

interface UserType {
  accessToken: string;
  idToken?: string;
  refreshToken: string;
  userId: string;
  userRole: string;
  userCompanyIds: string[];
  userEmail: string;
  userFullName: string;
}

interface AuthContextType {
  errors: string;
  handleLogin: (newState: any) => Promise<void>;
  logout: () => void;
  authorisedUser: UserType;
  submitting: boolean;
  firebaseOptions?: FirebaseOptions;
  firebaseApp?: FirebaseApp;
  firebaseAuth?: Auth;
  firestore?: Firestore;
  loginCredentials?: LogInCredentials;
}

interface LogInCredentials {
  email: string;
  password: string;
}

const initialValue = {
  authorisedUser: {
    accessToken: '',
    idToken: '',
    refreshToken: '',
    userId: '',
    userRole: '',
    userCompanyIds: [],
    userEmail: '',
    userFullName: '',
    titanRegionId: ''
  },
  handleLogin: () => Promise.resolve(),
  logout: () => {},
  errors: '',
  submitting: false
};

export const AuthContext = createContext<AuthContextType>(initialValue);

export const AuthProvider = ({ children }: Props) => {
  const navigate = useNavigate();
  const settings = useAppSettings();
  const [errors, setErrors] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [authorisedUser, setAuthorisedUser] = useLocalStorage('authorised-user', null);
  const [tokenRefreshTimerStart, tokenRefreshTimerStop, tokenRefreshTimerIsActive] = useTimer();
  
  const [firebaseOptions] = useState({
    apiKey: settings.firebaseApiKey,
    authDomain: settings.firebaseAuthDomain,
    projectId: settings.firebaseProjectId,
    storageBucket: settings.firebaseStorageBucket,
    messagingSenderId: settings.firebaseMessagingSenderId,
    appId: settings.firebaseAppId,
  });
  
  const firebaseApp = useMemo(() => initializeApp(firebaseOptions), [firebaseOptions]);
  const firebaseAuth = useMemo(() => getAuth(firebaseApp), [firebaseApp]);
  const firestore = useMemo(() => getFirestore(firebaseApp), [firebaseApp]);
  const [loginCredentials, setLoginCredentials] = useState<LogInCredentials>();
  
  const logout = useCallback(() => {
    
    signOut(firebaseAuth!);
    setAuthorisedUser(null);
    tokenRefreshTimerStop();
    
    const currentPath = window.location.pathname.toLowerCase();

    if (currentPath !== '/' && !currentPath.includes('/login') && !currentPath.includes('/sign-up')) {
      navigate('/', { replace: true });
    }

  }, [firebaseAuth, navigate, setAuthorisedUser, tokenRefreshTimerStop]);

  useEffect(() => {

    if (firebaseAuth.currentUser) {
      return;
    }

    const unsubscribe = onAuthStateChanged(firebaseAuth, async () => {
        
        if (firebaseAuth.currentUser) {
        
            let mainRole = "";
            const userRef = doc(firestore, "users", firebaseAuth.currentUser.uid);
            const userDoc = await getDoc(userRef);
            const userData = userDoc.exists() ? userDoc.data() : {};
            const roles = userData.profile?.roles || [];

            if (roles.includes('Admin')) {
                mainRole = 'Admin';
            } else if (roles.includes('Company-Admin')) {
                mainRole = 'Company-Admin';
            } else if (roles.includes('Technical-Agent')) {
                mainRole = 'Technical-Agent';
            } else if (roles.includes('Installer')) {
                mainRole = 'Installer';
            } else if (roles.includes('Account-Holder')) {
                mainRole = 'Account-Holder';
            }
            
            if (mainRole === 'Admin' || mainRole === 'Company-Admin' || mainRole === 'Technical-Agent' || mainRole === 'Installer') {

            setAuthorisedUser({
                userRole: mainRole,
                userId: firebaseAuth.currentUser.uid,
                userEmail: userData.profile?.email || '',
                userCompanyIds: userData.companyIds || [],
                refreshToken: firebaseAuth.currentUser.refreshToken,
                accessToken: await firebaseAuth.currentUser.getIdToken(),
                userFullName: `${userData.profile?.firstName} ${userData.profile?.lastName}`.trim(),
            });

            if (window.location.pathname.toLowerCase().includes('/login')) {
                if (mainRole === 'Company-Admin') {
                    navigate('/agent/companies');
                } else if (mainRole === 'Installer') {
                    navigate('/agent/installations');
                } else {
                    navigate('/agent');
                }
            }

            } else {

                logout();
                setErrors('Only administrative accounts are expected');

            }

        } else {
            
            logout();

        }
    });

    return () => unsubscribe();

  }, [firebaseAuth, setAuthorisedUser, firestore, logout, navigate]);

  if (!tokenRefreshTimerIsActive) {
    tokenRefreshTimerStart({
      interval: 10 * 1_000 * 50,
      callback: async () => {

        if (firebaseAuth.currentUser) {
          
          const accessToken = await firebaseAuth.currentUser.getIdToken(true);
          setAuthorisedUser((prev: any) => ({ ...prev || {}, accessToken: accessToken }));
          console.info("Token refreshed");

        }

      }
    });
  }

  async function handleLogin(data: LogInCredentials) {
    
    setErrors('');
    setSubmitting(true);

    try {
      
      setLoginCredentials(data);

      await setPersistence(firebaseAuth, browserSessionPersistence);
      await signInWithEmailAndPassword(firebaseAuth, data.email, data.password);
      
    } catch (error: any) {

      let message = error?.response?.data?.message || error.message || '';
      
      if (message.includes('invalid-email') || message.includes('invalid-credential')) {
        message = 'Invalid email or password';
      } else if (message.includes('network-request-failed')) {
        message = 'Network error';
      } else {
        message = message.replaceAll('FirebaseError:', '');
        message = message.replaceAll('Firebase:', '');
        message = message.replaceAll('Error (', '');
        message = message.replaceAll('auth/', '');
        message = message.replaceAll(').', '');
        message = message.trim();
      }
      
      setErrors(message);
    }

    setSubmitting(false);
  }

  return (
    <AuthContext.Provider value={{ authorisedUser, handleLogin, errors, submitting, logout, firebaseOptions, firebaseApp, firebaseAuth, firestore, loginCredentials }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  var context = useContext(AuthContext);
  try { context?.firebaseAuth?.currentUser?.getIdToken().catch(error => console.error('Error in useAuth : ', error)) } catch (e) { console.error('Error in useAuth : ', e) }
  return context;
};
